import { Alert, Card, SkeletonCard } from '@persona/ui';
import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AuditRow } from '../api';

/** Человеческие названия действий: `payment.refund` читается плохо. */
const ACTIONS: Record<string, string> = {
  'admin.login': 'Вход в панель',
  'payment.refund': 'Возврат платежа',
  'payment.refund_failed': 'Возврат не прошёл',
  'subscription.cancel': 'Отмена подписки',
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Журнал действий администраторов.
 *
 * Нужен для разбора спорных случаев: кто вернул деньги и почему. Доступен
 * только роли `admin` — журнал, который может подчистить любой сотрудник,
 * теряет смысл.
 */
export function AuditPage() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .audit()
      .then((result) => setRows(result.items))
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError
            ? cause.code === 'forbidden'
              ? 'Журнал доступен только администраторам.'
              : cause.message
            : 'Не удалось загрузить журнал',
        );
      });
  }, []);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold">Журнал действий</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted">
        Кто и что менял: входы в панель, возвраты и отмены. Записи не удаляются.
      </p>

      {error && (
        <Alert tone="danger" className="mt-6">
          {error}
        </Alert>
      )}

      {!rows && !error && (
        <div className="mt-8">
          <SkeletonCard />
        </div>
      )}

      {rows && rows.length === 0 && (
        <Card className="mt-8">
          <p className="text-sm text-muted">Пока ничего не записано.</p>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[var(--border-subtle)] text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2 font-medium">Когда</th>
                <th className="px-3 py-2 font-medium">Кто</th>
                <th className="px-3 py-2 font-medium">Действие</th>
                <th className="px-3 py-2 font-medium">Причина</th>
                <th className="px-3 py-2 font-medium">Адрес</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[var(--border-subtle)] align-top">
                  <td className="px-3 py-3 whitespace-nowrap text-muted">{formatDateTime(row.createdAt)}</td>
                  <td className="px-3 py-3">
                    {row.admin ? (
                      <>
                        <div className="font-medium">{row.admin.name}</div>
                        <div className="text-xs text-muted">{row.admin.email}</div>
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div>{ACTIONS[row.action] ?? row.action}</div>
                    {row.entityId && <div className="font-mono text-xs break-all text-muted">{row.entityId}</div>}
                  </td>
                  <td className="px-3 py-3 text-secondary">{row.reason ?? <span className="text-muted">—</span>}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-mono text-xs text-muted">{row.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
